import { request } from "@/axios";

export enum MemberType {
  Free = "free",
  Pro = "pro",
  Plus = "plus",
  Ultra = "ultra"
}

export enum UserRole {
  SuperAdmin = 1,
  Member = 2,
  SubAdmin = 3
}

export interface TeamInfo {
  /** 会员有效期开始时间 */
  startTime: string;
  /** 会员有效期结束时间 */
  endTime: string;
  /** 是否过期 */
  expire: boolean;
  /** 支持人员数量 */
  headCount: number;
  /** 会员类型：free-免费版 pro-标准版会员 plus-专业版会员 ultra-旗舰版会员 */
  memberType: `${MemberType}`;
  /** 团队名称 */
  name: string;
  /** 当前用户角色：1-超管 2-成员 */
  role: UserRole;
  /** 团队ID */
  teamId: number;
  /** 会员天数 */
  memberDay: number;
  /** 剩余天数 */
  leaveDay: number;
  /** 团队个人已使用空间 */
  personalSpace: number;
  /** 团队共享已使用空间 */
  shareSpace: number;
  /** 团队已使用总空间 */
  totalSpace: number;
  /** 团队总空间 */
  allSpace: number;
  /** 团队总算力 */
  totalPoints: number;
  /** 可用算力 */
  usablePoints: number;
}

/**
 * 获取团队信息
 */
export const getTeamInfo = () => {
  return request<TeamInfo>({
    api: "/api/team/teamInfo",
    method: "post"
  });
};

export interface TeamUser {
  /** 手机号 */
  account: string;
  /** 邮箱 */
  email: string;
  /** 加入状态 1-已加入 2-未加入 */
  joinStatus: number;
  /** 昵称 */
  nickname: string;
  /** 角色：1-超管，2-成员 */
  role: UserRole;
  /** 用户id */
  userId: number;
}

export interface TeamUserPage {
  /** 数据列表 */
  records: TeamUser[];
  /** 总数 */
  total: number;
}

export interface TeamUserInfo {
  /** 团队成员可容纳总数 */
  headCount: number;
  /** 加入成员数 */
  joinCount: number;
  /** 剩余成员数 */
  leftCount: number;
  /** 团队成员列表 */
  teamUserPage: TeamUserPage;
}

/**
 * 获取团队成员相关信息
 */
export const getTeamUserInfo = ({ pageNo, pageSize }: { pageNo?: number; pageSize?: number }) => {
  return request<TeamUserInfo>({
    api: "/api/team/teamUserInfo",
    method: "post",
    data: {
      pageNo,
      pageSize
    }
  });
};

export interface MemberVo {
  /** 时长 */
  duration: string;
  /** 结束时间 */
  endTime: string;
  /** 会员类型 */
  memberType: string;
  /** 签约编号 */
  signNo: string;
  /** 开始时间 */
  startTime: string;
}

export interface RightsInfo {
  /** 是否有过期会员记录 */
  expireMember: boolean;
  /** 当前是否是第一次买会员 */
  firstMember: boolean;
  /** 是否首单 */
  firstOrder: boolean;
  /** 是否首签 */
  firstSign: boolean;
  /**
   * SVIP 首期低价商品购买记录：0 未买（可买首期低价）/ 1 已买（走续费价）
   */
  svipFirstPurchaseLowPrice?: 0 | 1;
  /** 永久积分 */
  foreverPoints: number;
  /** 享受免费训练的id，是null说明没有享受过该权益 */
  freeTrainId: number | null;
  /** 是否添加了企微 */
  hasQiwei: boolean;
  /** 人员数量，个人版默认1 */
  headCount: number;
  /** 邀请剩余会员天数 */
  inviteLeaveDay: number;
  /** 邀请奖励会员天数 */
  inviteMemberDay: number;
  /** 邀请会员数 */
  inviteMembers: number;
  /** 邀请会员总通用算力 */
  invitePoints: number;
  /** 最后过期会员信息 */
  lastExpireMemberList: MemberVo[];
  /** 个人版下返回：最后过期团队会员信息 */
  lastExpireMemberTeamList: MemberVo[];
  /** 会员信息 */
  memberList: MemberVo[];
  /** 会员算力ddl */
  memberPointsDdl: string;
  /** 个人版下返回：团队会员信息 */
  memberTeamList: MemberVo[];
  /** 团队属性类型:1正式版 2试用版 */
  periodType: number;
  /** 总积分 */
  totalPoints: number;
  /** 总空间 单位M */
  totalSpace: number;
  /** 总快速 */
  totalSpeeds: number;
}

/**
 * 获取权益信息
 */
export const getRightsInfo = () => {
  return request<RightsInfo>({
    api: "/api/rights/getRights",
    method: "post"
  });
};

export interface TeamSpaceData {
  /** 团队id */
  id: number;
  /** 个人空间 */
  persionSize: number;
  /** 共享作品数 */
  shareArtworkCount: number;
  /** 共享模型数 */
  shareModelCount: number;
  /** 共享模型生图数 */
  shareModelImgCount: number;
  /** 团队空间 */
  teamSize: number;
}

/**
 * 获取团队共享协作数据
 */
export const getTeamGeneData = () => {
  return request<TeamSpaceData>({
    api: "/api/team/teamGeneData",
    method: "post"
  });
};

/**
 * 获取功能消耗统计
 */
export const getTeamPointsData = (data: any) => {
  return request<any>({
    api: "/api/team/teamPointsData",
    method: "post",
    data
  });
};

/**
 * 获取成员用量统计
 */
export const getTeamUserPointsData = (data: any) => {
  return request<any>({
    api: "/api/team/teamUserPointsData",
    method: "post",
    data
  });
};

/**
 * 用户自己退出团队
 */
export const exitTeamUser = () => {
  return request<any>({
    api: "/api/team/exitTeamUser",
    method: "get"
  });
};

/**
 * 保留当前选择成员
 */

export const retainTeamMembers = (data: { teamUserIds: any[] }) => {
  return request<any>({
    api: "/api/team/retainTeamMembers",
    method: "post",
    data
  });
};
